import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import PrivateLayout from "../../layouts/PrivateLayout";
import { fetchCharacterRaidStats, fetchCurrentRaidTeam } from "../../api/api";
import useDecodedToken from "../../utils/useDecodedToken";
import {
  formatAmount,
  formatRankPercent,
  getDifficultyLabel,
  getRankPercentStyle,
} from "./raidStatsUtils";

function RaidCharacterStats() {
  const backgroundImage = "/ww-wallpaper.webp";
  const navigate = useNavigate();
  const { characterId } = useParams();
  const { token } = useDecodedToken();

  const [stats, setStats] = useState([]);
  const [character, setCharacter] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const loadStats = useCallback(async () => {
    if (!token || !characterId) return;
    setIsLoading(true);
    setErrorMessage("");
    try {
      const data = await fetchCharacterRaidStats(token, characterId);
      setStats(Array.isArray(data) ? data : []);
    } catch (error) {
      setErrorMessage(error.message || "Failed to load character stats.");
    } finally {
      setIsLoading(false);
    }
  }, [token, characterId]);

  const loadCharacterInfo = useCallback(async () => {
    if (!token || !characterId) return;
    try {
      const team = await fetchCurrentRaidTeam(token);
      const member = (team?.members || []).find(
        (m) => m.characterId === characterId
      );
      setCharacter(member || null);
    } catch (error) {
      setCharacter(null);
    }
  }, [token, characterId]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  useEffect(() => {
    loadCharacterInfo();
  }, [loadCharacterInfo]);

  return (
    <PrivateLayout backgroundImage={backgroundImage}>
      <div className="flex flex-col items-center text-white bg-black/50 px-4 py-10 min-h-full">
        <div className="w-full max-w-5xl">
          <button
            type="button"
            onClick={() => navigate("/raid-statistics")}
            className="mb-6 flex items-center gap-2 text-sm text-gray-300 hover:text-white transition-colors"
          >
            <i className="fas fa-arrow-left"></i>
            Back to Raid Statistics
          </button>

          <div className="text-center mb-8">
            <h1 className="text-2xl md:text-4xl font-bold drop-shadow-lg mb-2">
              {character?.characterName || "Character Stats"}
            </h1>
            {character?.realm && (
              <p className="text-gray-300 text-sm md:text-base">
                {character.realm}
              </p>
            )}
          </div>

          {isLoading && (
            <div className="flex items-center justify-center py-20">
              <div className="w-10 h-10 border-4 border-blue-400 border-t-transparent rounded-full animate-spin"></div>
            </div>
          )}

          {!isLoading && errorMessage && (
            <div className="bg-red-500/20 border border-red-500/40 rounded-lg px-4 py-3 text-red-300 text-sm text-center">
              {errorMessage}
            </div>
          )}

          {!isLoading && !errorMessage && stats.length === 0 && (
            <div className="text-center py-16 bg-white/5 border border-white/10 rounded-xl">
              <i className="fas fa-chart-line text-4xl text-gray-600 mb-3 block"></i>
              <p className="text-gray-400">
                No ranked parses available yet for this character.
              </p>
            </div>
          )}

          {!isLoading && !errorMessage && stats.length > 0 && (
            <div className="rounded-xl overflow-hidden border border-white/10 shadow-2xl">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-white/90 text-gray-500 text-xs uppercase tracking-widest">
                      <th className="text-left px-4 py-3 font-semibold">Boss</th>
                      <th className="text-left px-4 py-3 font-semibold">
                        Difficulty
                      </th>
                      <th className="text-left px-4 py-3 font-semibold">
                        Metric
                      </th>
                      <th className="text-right px-4 py-3 font-semibold">
                        Best Amount
                      </th>
                      <th className="text-right px-4 py-3 font-semibold">
                        Best Rank %
                      </th>
                      <th className="text-right px-4 py-3 font-semibold">
                        Pulls
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {stats.map((entry, idx) => (
                      <tr
                        key={`${entry.encounterName}-${entry.difficulty}-${entry.metric}-${idx}`}
                        className="bg-white/5 hover:bg-white/10 transition-colors"
                      >
                        <td className="px-4 py-3 font-medium">
                          {entry.encounterName}
                        </td>
                        <td className="px-4 py-3 text-gray-300">
                          {getDifficultyLabel(entry.difficulty)}
                        </td>
                        <td className="px-4 py-3 text-gray-300 uppercase">
                          {entry.metric}
                        </td>
                        <td className="px-4 py-3 text-right text-white">
                          {formatAmount(entry.bestAmount)}
                        </td>
                        <td
                          className={`px-4 py-3 text-right ${getRankPercentStyle(entry.bestRankPercent)}`}
                        >
                          {formatRankPercent(entry.bestRankPercent)}
                        </td>
                        <td className="px-4 py-3 text-right text-gray-300">
                          {entry.pulls}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </PrivateLayout>
  );
}

export default RaidCharacterStats;
